import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDateTime } from '../utils/dateFormat';

const moduleOptions = [
  { value: 'customer', label: 'Customer' },
  { value: 'sale', label: 'Sale' },
  { value: 'payment', label: 'Payment' }
];

const paymentModes = [
  { value: 'cash', label: 'Cash' },
  { value: 'credit', label: 'Credit' },
  { value: 'online', label: 'Online' },
  { value: 'upi', label: 'UPI' },
  { value: 'gpay', label: 'GPay' },
  { value: 'paytm', label: 'Paytm' },
  { value: 'phonepe', label: 'PhonePe' }
];

const fieldConfig = {
  customer: [
    { key: 'name', label: 'Customer Name', type: 'text' },
    { key: 'mobile', label: 'Mobile', type: 'text' },
    { key: 'driver_name', label: 'Driver Name', type: 'text' },
    { key: 'address', label: 'Address', type: 'text' },
    { key: 'vehicle_no', label: 'Vehicle Number', type: 'text' },
    { key: 'device_type', label: 'Device Type', type: 'text' },
    { key: 'device_model', label: 'Device Model', type: 'text' },
    { key: 'imei', label: 'IMEI', type: 'text' },
    { key: 'sim_no', label: 'SIM Number', type: 'text' },
    { key: 'sim_no_2', label: 'SIM Number 2', type: 'text' },
    { key: 'installation_date', label: 'Installation Date', type: 'date' },
    { key: 'validity_months', label: 'Validity Months', type: 'number' },
    { key: 'renewal_date', label: 'Renewal Date', type: 'date' },
    { key: 'payment_status', label: 'Payment Status', type: 'select', options: [
      { value: 'pending', label: 'Pending' },
      { value: 'partial', label: 'Partial' },
      { value: 'paid', label: 'Paid' }
    ] },
    { key: 'gps_tracking_enabled', label: 'GPS Tracking', type: 'select', options: [
      { value: '0', label: 'Disabled' },
      { value: '1', label: 'Enabled' }
    ] }
  ],
  sale: [
    { key: 'sale_price', label: 'Sale Price', type: 'money' },
    { key: 'installation_charge', label: 'Installation Charge', type: 'money' },
    { key: 'paid_amount', label: 'Paid Amount', type: 'money' },
    { key: 'sale_type', label: 'Sale Type', type: 'select', options: [
      { value: 'direct', label: 'Direct' },
      { value: 'dealer', label: 'Dealer' },
      { value: 'employee', label: 'Employee' },
      { value: 'admin', label: 'Admin' }
    ] },
    { key: 'notes', label: 'Notes', type: 'text' }
  ],
  payment: [
    { key: 'amount', label: 'Amount', type: 'money' },
    { key: 'payment_mode', label: 'Payment Mode', type: 'select', options: paymentModes },
    { key: 'upi_ref', label: 'Transaction Reference', type: 'text' },
    { key: 'note', label: 'Note', type: 'text' }
  ]
};

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const fieldLabel = (moduleType, key) => (
  (fieldConfig[moduleType] || []).find((field) => field.key === key)?.label || key
);

const getField = (moduleType, key) => (fieldConfig[moduleType] || []).find((field) => field.key === key);

const displayValue = (moduleType, key, value) => {
  if (value === null || value === undefined || value === '') return '-';
  const field = getField(moduleType, key);
  if (field?.key === 'gps_tracking_enabled') return value === 1 || value === '1' ? 'Enabled' : 'Disabled';
  if (field?.type === 'money') return `Rs. ${Number(value || 0).toFixed(2)}`;
  if (field?.type === 'select') {
    return field.options?.find((option) => String(option.value) === String(value))?.label || String(value);
  }
  return String(value);
};

const normalizeInputValue = (value) => (value === null || value === undefined ? '' : String(value));

export default function CorrectionRequests() {
  const user = useMemo(() => getStoredUser(), []);
  const isEmployee = user?.role === 'employee';
  const canDecide = ['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role);
  const [moduleType, setModuleType] = useState('customer');
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState('');
  const [formData, setFormData] = useState({});
  const [reason, setReason] = useState('');
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [decisionNotes, setDecisionNotes] = useState({});

  const selectedRecord = useMemo(() => (
    records.find((record) => String(record.id) === String(recordId)) || null
  ), [recordId, records]);

  const visibleRequests = useMemo(() => (
    statusFilter === 'all' ? requests : requests.filter((item) => item.status === statusFilter)
  ), [requests, statusFilter]);

  const fetchRequests = useCallback(async () => {
    const res = await axios.get(`${API_URL}/api/corrections`, { headers: getHeaders() });
    setRequests(res.data || []);
  }, []);

  const fetchContext = useCallback(async () => {
    if (!isEmployee) return;
    const res = await axios.get(`${API_URL}/api/corrections/context?module_type=${moduleType}`, { headers: getHeaders() });
    const rows = res.data.records || [];
    setRecords(rows);
    const first = rows[0] || null;
    setRecordId(first ? String(first.id) : '');
    setFormData(first?.data || {});
  }, [isEmployee, moduleType]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchRequests(),
        fetchContext()
      ]);
    } catch (err) {
      alert(err.response?.data?.message || 'Correction requests load failed');
    } finally {
      setLoading(false);
    }
  }, [fetchContext, fetchRequests]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!selectedRecord) {
      setFormData({});
      return;
    }
    setFormData(selectedRecord.data || {});
  }, [selectedRecord]);

  const updateField = (key, value) => {
    setFormData((current) => ({ ...current, [key]: value }));
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    if (!recordId) return alert('Select record first');
    if (!reason.trim()) return alert('Correction reason is required');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/corrections`, {
        module_type: moduleType,
        record_id: recordId,
        requested_data: formData,
        reason
      }, { headers: getHeaders() });
      alert('Correction request submitted');
      setReason('');
      await fetchRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Correction request failed');
    } finally {
      setSaving(false);
    }
  };

  const decideRequest = async (request, decision) => {
    const actionText = decision === 'approve' ? 'approve' : 'reject';
    if (!window.confirm(`${actionText} correction request #${request.id}?`)) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/corrections/${request.id}/${decision}`, {
        decision_note: decisionNotes[request.id] || ''
      }, { headers: getHeaders() });
      alert(decision === 'approve' ? 'Correction approved' : 'Correction rejected');
      await fetchRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Correction decision failed');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (field) => {
    const value = normalizeInputValue(formData[field.key]);
    if (field.type === 'select') {
      return (
        <select style={inp} value={value} onChange={(event) => updateField(field.key, event.target.value)}>
          {(field.options || []).map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type === 'date' ? 'date' : field.type === 'money' || field.type === 'number' ? 'number' : 'text'}
        step={field.type === 'money' ? '0.01' : undefined}
        min={field.type === 'money' || field.type === 'number' ? '0' : undefined}
        style={inp}
        value={value}
        onChange={(event) => updateField(field.key, event.target.value)}
      />
    );
  };

  const renderRequestDiff = (request) => {
    const entries = Object.keys(request.requested_data || {});
    if (entries.length === 0) return <span style={{ color: '#64748b' }}>-</span>;
    return (
      <div style={{ display: 'grid', gap: '8px' }}>
        {entries.map((key) => (
          <div key={key} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '9px' }}>
            <strong style={{ display: 'block', color: '#0f172a', fontSize: '12px', marginBottom: '4px' }}>{fieldLabel(request.module_type, key)}</strong>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
              <span style={{ color: '#991b1b', overflowWrap: 'anywhere' }}>Old: {displayValue(request.module_type, key, request.old_data?.[key])}</span>
              <span style={{ color: '#047857', overflowWrap: 'anywhere' }}>New: {displayValue(request.module_type, key, request.requested_data?.[key])}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const stats = [
    { label: 'Pending', value: requests.filter((item) => item.status === 'pending').length, color: '#d97706' },
    { label: 'Approved', value: requests.filter((item) => item.status === 'approved').length, color: '#047857' },
    { label: 'Rejected', value: requests.filter((item) => item.status === 'rejected').length, color: '#dc2626' }
  ];

  if (!isEmployee && !canDecide) {
    return (
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '24px', color: '#64748b' }}>
        Correction requests are not available for this role.
      </div>
    );
  }

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <div>
            <h2 style={{ color: '#1e3a5f', margin: '0 0 6px' }}>Correction Requests</h2>
            <p style={{ color: '#64748b', margin: 0, fontSize: '13px' }}>
              {isEmployee ? 'My submitted corrections' : 'Employee correction approvals'}
            </p>
          </div>
          <button type="button" onClick={reload} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: '#f1f5f9', color: '#0f172a', border: '1px solid #dbe3ef', padding: '9px 14px', borderRadius: '8px', cursor: loading ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
            <RefreshCw size={15} /> {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '18px' }}>
          {stats.map((item) => (
            <div key={item.label} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', borderLeft: `4px solid ${item.color}` }}>
              <span style={{ display: 'block', color: '#64748b', fontSize: '12px', fontWeight: 800, marginBottom: '6px' }}>{item.label}</span>
              <strong style={{ color: item.color, fontSize: '24px' }}>{item.value}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="fixed-panel-body">
        {isEmployee && (
          <section style={sectionStyle}>
            <h3 style={sectionTitle}>New Correction Request</h3>
            <form onSubmit={submitRequest}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                <label style={labelStyle}>Module
                  <select style={inp} value={moduleType} onChange={(event) => setModuleType(event.target.value)}>
                    {moduleOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>Record
                  <select style={inp} value={recordId} onChange={(event) => setRecordId(event.target.value)}>
                    <option value="">Select record</option>
                    {records.map((record) => <option key={record.id} value={record.id}>{record.label}</option>)}
                  </select>
                </label>
                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>Reason
                  <input style={inp} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Reason for correction" />
                </label>
              </div>

              {selectedRecord && (
                <div style={{ marginTop: '16px' }}>
                  <h4 style={{ color: '#0f766e', margin: '0 0 10px', fontSize: '14px' }}>Correct Values</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                    {(fieldConfig[moduleType] || []).map((field) => (
                      <label key={field.key} style={labelStyle}>{field.label}
                        {renderInput(field)}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <button type="submit" disabled={saving || !selectedRecord} style={{ marginTop: '16px', background: saving || !selectedRecord ? '#94a3b8' : '#0f766e', color: 'white', border: 'none', borderRadius: '8px', padding: '11px 18px', cursor: saving || !selectedRecord ? 'wait' : 'pointer', fontWeight: 800 }}>
                {saving ? 'Submitting...' : 'Submit Correction'}
              </button>
            </form>
          </section>
        )}

        <section style={{ ...sectionStyle, marginTop: isEmployee ? '18px' : 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <h3 style={sectionTitle}>{isEmployee ? 'My Requests' : 'Approval Queue'}</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {['pending', 'approved', 'rejected', 'all'].map((status) => (
                <button key={status} type="button" onClick={() => setStatusFilter(status)} style={{ background: statusFilter === status ? '#1e3a5f' : 'white', color: statusFilter === status ? 'white' : '#1e3a5f', border: '1px solid #dbe3ef', borderRadius: '999px', padding: '7px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 800, textTransform: 'capitalize' }}>
                  {status}
                </button>
              ))}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: canDecide ? '1180px' : '920px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', color: '#334155' }}>
                  {['ID', 'Module', 'Record', 'Requested By', 'Change', 'Reason', 'Status', 'Date', canDecide ? 'Decision' : 'Note'].map((head) => (
                    <th key={head} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', borderBottom: '1px solid #e5e7eb' }}>{head}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" style={emptyCell}>Loading correction requests...</td></tr>
                ) : visibleRequests.length === 0 ? (
                  <tr><td colSpan="9" style={emptyCell}>No correction requests found.</td></tr>
                ) : visibleRequests.map((request) => (
                  <tr key={request.id} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                    <td style={tdStyle}>#{request.id}</td>
                    <td style={tdStyle}>{request.module_label}</td>
                    <td style={{ ...tdStyle, minWidth: '180px' }}>{request.record_label || `#${request.record_id}`}</td>
                    <td style={tdStyle}>{request.requested_by_name || '-'}</td>
                    <td style={{ ...tdStyle, minWidth: '280px' }}>{renderRequestDiff(request)}</td>
                    <td style={{ ...tdStyle, minWidth: '180px', overflowWrap: 'anywhere' }}>{request.reason}</td>
                    <td style={tdStyle}>
                      <span style={{ ...statusPill, ...statusStyle(request.status) }}>{request.status}</span>
                    </td>
                    <td style={tdStyle}>{request.created_at ? formatDateTime(request.created_at) : '-'}</td>
                    <td style={{ ...tdStyle, minWidth: canDecide ? '220px' : '160px' }}>
                      {canDecide && request.status === 'pending' ? (
                        <div style={{ display: 'grid', gap: '8px' }}>
                          <input style={inp} value={decisionNotes[request.id] || ''} onChange={(event) => setDecisionNotes((current) => ({ ...current, [request.id]: event.target.value }))} placeholder="Decision note" />
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => decideRequest(request, 'approve')} disabled={saving} style={decisionButton('#047857')}>
                              <CheckCircle2 size={15} /> Approve
                            </button>
                            <button type="button" onClick={() => decideRequest(request, 'reject')} disabled={saving} style={decisionButton('#dc2626')}>
                              <XCircle size={15} /> Reject
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#64748b', fontSize: '12px' }}>{request.decision_note || request.decided_by_name || '-'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

const inp = {
  width: '100%',
  padding: '10px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '13px',
  boxSizing: 'border-box',
  marginTop: '6px'
};

const labelStyle = {
  display: 'block',
  color: '#334155',
  fontSize: '12px',
  fontWeight: 800
};

const sectionStyle = {
  background: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
};

const sectionTitle = {
  color: '#1e3a5f',
  margin: '0 0 14px',
  fontSize: '18px'
};

const tdStyle = {
  padding: '10px 12px',
  fontSize: '12px',
  color: '#334155'
};

const emptyCell = {
  padding: '24px',
  textAlign: 'center',
  color: '#64748b',
  fontSize: '13px'
};

const statusPill = {
  display: 'inline-block',
  borderRadius: '999px',
  padding: '4px 9px',
  fontSize: '11px',
  fontWeight: 900,
  textTransform: 'capitalize'
};

const statusStyle = (status) => {
  if (status === 'approved') return { background: '#d1fae5', color: '#047857' };
  if (status === 'rejected') return { background: '#fee2e2', color: '#dc2626' };
  return { background: '#fef3c7', color: '#b45309' };
};

const decisionButton = (color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  border: 'none',
  borderRadius: '8px',
  background: color,
  color: 'white',
  padding: '8px 10px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 800
});
